import React, { useEffect, useState } from "react";
import { ArrowLeft, Download, Forward, Loader2, Paperclip, Reply, ReplyAll } from "lucide-react";
import toast from "react-hot-toast";

import { downloadAttachment, getThread } from "../api/mailboxApi";
import { forwardDraft, formatMailDate, formatSize, personLabel, replyDraft } from "../utils/mail";
import MessageBody from "./MessageBody";

const actionClass =
  "inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50";

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

function Message({ message, expanded, onToggle, onCompose, ownEmail }) {
  const [downloading, setDownloading] = useState("");

  const download = async (a) => {
    setDownloading(a.part_id);
    try {
      saveBlob(await downloadAttachment(message.id, a.part_id), a.filename);
    } catch {
      toast.error(`Could not download ${a.filename}.`);
    } finally {
      setDownloading("");
    }
  };

  const recipients = [...message.to, ...message.cc];
  return (
    <article className="border-b border-gray-100 last:border-b-0">
      <button type="button" onClick={onToggle} className="w-full px-4 py-3 text-left hover:bg-gray-50">
        <div className="flex items-baseline justify-between gap-3">
          <span className={`truncate text-sm ${message.unread ? "font-semibold" : "font-medium"} text-gray-900`}>
            {message.from.name || message.from.email}
          </span>
          <span className="shrink-0 text-xs text-gray-500">{formatMailDate(message.date)}</span>
        </div>
        {expanded ? (
          <p className="mt-0.5 truncate text-xs text-gray-500">
            to {recipients.map((p) => p.name || p.email).join(", ") || "(no recipients)"}
          </p>
        ) : (
          <p className="mt-0.5 truncate text-sm text-gray-500">{message.snippet}</p>
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4">
          <p className="mb-3 break-all text-xs text-gray-500">From {personLabel(message.from)}</p>
          <MessageBody html={message.body_html} text={message.body_text} />

          {message.attachments.length > 0 && (
            <ul className="mt-3 flex flex-wrap gap-2" aria-label="Attachments">
              {message.attachments.map((a) => (
                <li key={a.part_id}>
                  <button type="button" onClick={() => download(a)} disabled={downloading === a.part_id}
                    className="inline-flex max-w-[16rem] items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100">
                    {downloading === a.part_id
                      ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                      : <Paperclip className="h-4 w-4 shrink-0" />}
                    <span className="truncate">{a.filename}</span>
                    <span className="shrink-0 text-xs text-gray-400">{formatSize(a.size)}</span>
                    <Download className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" className={actionClass} onClick={() => onCompose(replyDraft(message, ownEmail))}>
              <Reply className="h-4 w-4" /> Reply
            </button>
            <button type="button" className={actionClass} onClick={() => onCompose(replyDraft(message, ownEmail, true))}>
              <ReplyAll className="h-4 w-4" /> Reply all
            </button>
            <button type="button" className={actionClass} onClick={() => onCompose(forwardDraft(message))}>
              <Forward className="h-4 w-4" /> Forward
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

/**
 * One conversation, read live from Gmail. The newest message is open; older
 * ones show a one-line preview until clicked. `reloadKey` changes after a
 * reply is sent so the new message appears.
 */
export default function ThreadView({ threadId, ownEmail, onBack, onCompose, reloadKey }) {
  const [thread, setThread] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(new Set());

  useEffect(() => {
    // Only the latest request may fill the pane: switching conversations
    // quickly must never show (or reply to) the previous one.
    let current = true;
    setThread(null);
    setError("");
    getThread(threadId).then((t) => {
      if (!current) return;
      setThread(t);
      setOpen(new Set(t.messages.length ? [t.messages[t.messages.length - 1].id] : []));
    }).catch((err) => {
      if (!current) return;
      setError(err?.response?.status === 404
        ? "This conversation is no longer in your Gmail."
        : "Could not load this conversation. Please try again.");
    });
    return () => {
      current = false;
    };
  }, [threadId, reloadKey]);

  const toggle = (id) => {
    const next = new Set(open);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setOpen(next);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
        <button type="button" onClick={onBack} aria-label="Back to the list"
          className="rounded-md p-1 text-gray-500 hover:bg-gray-100 md:hidden">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h2 className="min-w-0 flex-1 truncate text-base font-medium text-gray-900">
          {thread ? thread.subject || "(no subject)" : ""}
        </h2>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {error ? (
          <p role="alert" className="p-4 text-sm text-red-600">{error}</p>
        ) : !thread ? (
          <p className="flex items-center gap-2 p-4 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading conversation…
          </p>
        ) : (
          thread.messages.map((m) => (
            <Message key={m.id} message={m} ownEmail={ownEmail} expanded={open.has(m.id)}
              onToggle={() => toggle(m.id)} onCompose={onCompose} />
          ))
        )}
      </div>
    </div>
  );
}
